import {SPECIAL_BUILDING_TOKENS, TOKENS} from "../../../constants/tokens";
import {calcGuildIncome} from "../../gameState/income/calcGuildIncome";
import {calcTempleIncome} from "../../gameState/income/calcTempleIncome";
import {checkIfOwner} from "../../gameState/checkIfOwner";
import {keyToCoords} from "../../coords/keyToCoords";
import {INCOME_INFLUENCE_PATH_LENGTH} from "../../../configs/income";

/**
 * Города всех других игроков (включая нейтральные) — формат enemyCities
 * из updatePlayersIncome для калькуляторов доходов влияния.
 */
export const getEnemyCitiesForInfluence = (citiesCache, currentPlayerColor) => {
  return Object.entries(citiesCache || {})
    .filter(([color]) => color !== currentPlayerColor)
    .flatMap(([, cities]) => cities);
};

/**
 * Свои гильдии и храмы: [{hexKey, token}].
 */
export const getOwnInfluenceBuildings = (specialBuildings, currentPlayerColor, citiesCache) => {
  return Object.entries(specialBuildings || {})
    .filter(([hexKey, token]) =>
      (token === SPECIAL_BUILDING_TOKENS.GUILD || token === SPECIAL_BUILDING_TOKENS.TEMPLE)
      && checkIfOwner(hexKey, currentPlayerColor, citiesCache)
    )
    .map(([hexKey, token]) => ({hexKey, token}));
};

const sumInfluenceIncome = (influenceBuildings, player, enemyCities, hexes) => {
  return influenceBuildings.reduce((sum, building) => {
    if (building.token === SPECIAL_BUILDING_TOKENS.GUILD) {
      return sum + calcGuildIncome(building.hexKey, player, enemyCities, hexes);
    }
    return sum + calcTempleIncome(building.hexKey, player, enemyCities, hexes);
  }, 0);
};

/**
 * Прирост дохода гильдий/храмов игрока, если построить дорогу в roadCellKey
 * (нюанс #3: дороги ради путей влияния). Калькуляторы доходов чистые по hexes —
 * гипотетическую дорогу оцениваем на клоне карты.
 */
export const calcRoadInfluenceIncomeDelta = (roadCellKey, player, influenceBuildings, enemyCities, hexes) => {
  if (influenceBuildings.length === 0) {
    return 0;
  }

  const baseline = sumInfluenceIncome(influenceBuildings, player, enemyCities, hexes);
  const hypotheticalHexes = {...hexes, [roadCellKey]: TOKENS.ROAD};
  const withRoad = sumInfluenceIncome(influenceBuildings, player, enemyCities, hypotheticalHexes);

  return withRoad - baseline;
};

/**
 * Проекция дохода гильдии/храма при размещении на ячейке cellKey
 * (пути влияния считаются от ячейки — само строение для расчета не требуется).
 */
export const projectInfluenceBuildingIncome = (token, cellKey, player, enemyCities, hexes) => {
  if (token === SPECIAL_BUILDING_TOKENS.GUILD) {
    return calcGuildIncome(cellKey, player, enemyCities, hexes);
  }
  return calcTempleIncome(cellKey, player, enemyCities, hexes);
};

const calcBuildingInfluenceIncome = (building, player, enemyCities, hexes) => (
  building.token === SPECIAL_BUILDING_TOKENS.GUILD
    ? calcGuildIncome(building.hexKey, player, enemyCities, hexes)
    : calcTempleIncome(building.hexKey, player, enemyCities, hexes)
);

// Расстояние по сетке между ячейками. Только для целых координат, которые
// складываются точно: для прочих — NaN, и строение считается в досягаемости
// (полный пересчет, как без отсечения)
const calcHexDistance = (fromHexKey, toHexKey) => {
  const [fromQ, fromR] = keyToCoords(fromHexKey);
  const [toQ, toR] = keyToCoords(toHexKey);
  if (![fromQ, fromR, toQ, toR].every(Number.isSafeInteger)) {
    return NaN;
  }
  const dq = fromQ - toQ;
  const dr = fromR - toR;
  return (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2;
};

/**
 * То же, что calcRoadInfluenceIncomeDelta(roadCellKey, ...), для многих ячеек
 * подряд на одной карте — бот оценивает так каждую доступную дорогу. Карта,
 * строения, города и игрок между вызовами не должны меняться.
 *
 * Ответ тот же, а работы меньше:
 *  - доход без дороги одинаков для всех ячеек — считается один раз;
 *  - путь влияния читает карту только в пределах INCOME_INFLUENCE_PATH_LENGTH
 *    шагов от строения (см. calculateInfluencePathIncome), а шаг пути не
 *    короче расстояния по сетке: дальше этого дорога доход строения не меняет,
 *    и для такого строения берется его доход на той же карте без дороги;
 *  - карта с дорогой — одна копия на все ячейки: дорога ставится на время
 *    расчета и снимается, а доходы карту только читают
 * @returns {(roadCellKey: string) => number}
 */
export const createRoadInfluenceIncomeDeltaCalculator = (player, influenceBuildings, enemyCities, hexes) => {
  if (influenceBuildings.length === 0) {
    return () => 0;
  }

  let baseline = null;
  let hypotheticalHexes = null;
  let incomesWithoutRoad = null;

  return (roadCellKey) => {
    // Нестроковый ключ — прежним расчетом, как есть
    if (typeof roadCellKey !== 'string') {
      return calcRoadInfluenceIncomeDelta(roadCellKey, player, influenceBuildings, enemyCities, hexes);
    }

    if (baseline === null) {
      baseline = sumInfluenceIncome(influenceBuildings, player, enemyCities, hexes);
      hypotheticalHexes = {...hexes};
      incomesWithoutRoad = influenceBuildings.map((building) =>
        calcBuildingInfluenceIncome(building, player, enemyCities, hypotheticalHexes));
    }

    const isRoadInReach = (building) => !(calcHexDistance(building.hexKey, roadCellKey) > INCOME_INFLUENCE_PATH_LENGTH);
    const isAnyBuildingInReach = influenceBuildings.some(isRoadInReach);

    const hadCell = Object.prototype.hasOwnProperty.call(hypotheticalHexes, roadCellKey);
    const previousToken = hypotheticalHexes[roadCellKey];
    if (isAnyBuildingInReach) {
      hypotheticalHexes[roadCellKey] = TOKENS.ROAD;
    }
    let withRoad;
    try {
      withRoad = influenceBuildings.reduce((sum, building, index) => sum + (isAnyBuildingInReach && isRoadInReach(building)
        ? calcBuildingInfluenceIncome(building, player, enemyCities, hypotheticalHexes)
        : incomesWithoutRoad[index]), 0);
    } finally {
      if (isAnyBuildingInReach) {
        if (hadCell) {
          hypotheticalHexes[roadCellKey] = previousToken;
        } else {
          delete hypotheticalHexes[roadCellKey];
        }
      }
    }

    return withRoad - baseline;
  };
};
